import React, { useEffect, useRef } from "react";
import { Box, Card, IconButton, Typography } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";

// A single bookmark tile. Mirrors the glass-card styling of the old dashboard
// shortcut grid, but the icon is always a generic bookmark (the tile is a
// user-curated link, not a fixed route). In manage mode the card becomes a
// drag handle (fixed-slot reorder via dnd-kit) and shows an X to delete; in
// normal mode it navigates on click.
export default function BookmarkCard({
  id,
  node,
  parentLabel,
  Icon,
  // Gated away for the selected school: greyed and non-navigable in normal mode,
  // but still fully manageable (drag/reorder/remove) in manage mode.
  disabled = false,
  manageMode,
  highlighted,
  focused,
  setItemRef,
  onFocus,
  onKeyDown,
  onActivate,
  onRemove,
  onLongPress,
  padding = 1,
  // Surface treatment:
  //   "glass"   - the light glass card (default).
  //   "dark"    - translucent-white card on a dark background, white icon/text,
  //               tighter corner radius.
  //   "frosted" - the design's tile: a frosted pane with a translucent white
  //               rim. Used by the launchpad and the Home screen alike.
  variant = "glass",
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !manageMode });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Press-and-hold (outside manage mode) enters the bookmarks "manage" state.
  // We start a timer on pointer down and, if it fires before the press ends,
  // call onLongPress and flag the gesture so the trailing click does not also
  // navigate.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const startLongPress = () => {
    if (manageMode || !onLongPress) return;
    longPressFired.current = false;
    clearLongPress();
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onLongPress();
    }, 500);
  };

  useEffect(() => clearLongPress, []);

  // A disabled tile only behaves disabled outside manage mode; in manage mode it
  // must stay draggable/removable. `dimmed` greys it, `interactive` gates the
  // navigate click + hover affordance.
  const disabledNow = disabled && !manageMode;
  const dimmed = isDragging || disabledNow;
  const interactive = !manageMode && !disabled;
  const disabledCursor = disabled ? "not-allowed" : "pointer";

  const dark = variant === "dark";
  const frosted = variant === "frosted";
  // Tiles on a colored surface carry white content; the light glass card keeps
  // the muted-on-white treatment.
  const onColor = dark || frosted;
  // Keyboard focus (arrow keys roam the grid) and the just-added highlight both
  // ring the tile. The themed blue is for the light card only - on a colored
  // surface it fights the background, so those tiles ring in white.
  const ringColor = onColor ? "#FFFFFF" : "primary.main";

  // Hover treatment: frosted tiles swap their translucent white fill for a dark
  // wash, per the design; dark tiles deepen to just below the page background
  // (#263238); light glass tiles use the subtle brightness dip. Hoisted to
  // variables to keep the sx block free of nested ternaries.
  const hoverByVariant = {
    glass: { filter: "brightness(0.97)" },
    dark: { background: "#202A30" },
    frosted: { background: "rgba(25, 25, 25, 0.41)" },
  };
  const hoverSx = interactive ? hoverByVariant[variant] : {};

  // Per-variant surface. The frosted tile is a translucent white fill behind a
  // 1px white rim, with no glass gradient or rim shadow (both read as dirt on a
  // colored surface).
  const surfaceByVariant = {
    glass: { borderRadius: 8 }, // double the glassBox default (4)
    dark: {
      background: "rgba(255, 255, 255, 0.08)",
      border: "none",
      boxShadow: "none",
      borderRadius: "16px",
      "&::before": { display: "none" },
    },
    frosted: {
      background: "rgba(255, 255, 255, 0.14)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      // The glass rim, same idea as glassBox: lit from the bottom-right, so a
      // white glow rides the bottom/right inner edges and a dark one the
      // top/left. Softer than the light card's, since the tile is translucent
      // and a full-strength white would fog the color behind it.
      boxShadow: [
        "inset -6px -6px 8px 0px rgba(255, 255, 255, 0.03)",
        "inset 6px 6px 8px 0px rgba(0, 0, 0, 0.05)",
      ].join(", "),
      borderRadius: "20px",
      "&::before": { display: "none" },
    },
  };

  const iconSize = frosted ? 29 : 32;

  // The frosted tile stacks its content from the top (icon sitting low in the
  // upper half, label filling the rest) rather than centering the pair. Its
  // padding is a percentage of the tile width - which, on a square, is also its
  // height - so the icon keeps sitting at the same point of the tile whatever
  // width the grid hands it (4 across on desktop, 3 on a phone).
  const layoutSx = frosted
    ? {
        justifyContent: "flex-start",
        gap: "6px",
        p: "25% 8% 6%",
      }
    : { justifyContent: "center", gap: 0.75, p: padding };

  return (
    // dnd-kit's translate lives on this wrapper; the wiggle rotation lives on
    // the Card inside. Keeping them on separate elements lets the two transforms
    // compose instead of overwriting each other (which would break either the
    // reorder animation or the wiggle).
    <Box
      ref={setNodeRef}
      style={dragStyle}
      sx={{
        position: "relative",
        // The square is defined here, on the grid cell itself, and the card
        // below fills it. `alignSelf: start` keeps the cell at its ratio height
        // instead of stretching to the tallest tile in the row.
        aspectRatio: "1",
        boxSizing: "border-box",
        minWidth: 0,
        alignSelf: "start",
      }}
    >
      <Card
        ref={setItemRef}
        role="gridcell"
        tabIndex={manageMode || focused ? 0 : -1}
        aria-label={manageMode ? `Reorder ${node.label} bookmark` : node.label}
        aria-disabled={disabledNow || undefined}
        onClick={() => {
          if (manageMode) return;
          // Swallow the click that ends a long-press so it does not navigate.
          if (longPressFired.current) {
            longPressFired.current = false;
            return;
          }
          // Gated away at this school: not navigable (still manageable above).
          if (disabled) return;
          onActivate(node);
        }}
        onPointerDown={startLongPress}
        onPointerUp={clearLongPress}
        onPointerLeave={clearLongPress}
        onPointerCancel={clearLongPress}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        {...(manageMode ? attributes : {})}
        {...(manageMode ? listeners : {})}
        sx={(theme) => ({
          ...theme.custom.glassBox,
          ...surfaceByVariant[variant],
          // Fill the square wrapper exactly. box-sizing has to be set here: the
          // app has no global border-box reset, so without it the padding and
          // border are added ON TOP of the 100% height and the tile ends up
          // taller than it is wide (the more padding, the taller). Overflow is
          // clipped so a long label can never push the tile out of shape either.
          boxSizing: "border-box",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          ...layoutSx,
          cursor: manageMode ? "grab" : disabledCursor,
          opacity: dimmed ? 0.4 : 1,
          touchAction: manageMode ? "none" : "auto",
          userSelect: "none",
          transition:
            "background 160ms, opacity 120ms, filter 120ms, box-shadow 200ms, outline-color 200ms",
          // Lift the tile while it is actually being dragged.
          ...(isDragging && { cursor: "grabbing", boxShadow: 6, zIndex: 1 }),
          // Wiggle every tile while managing (except the one being dragged), the
          // way phone home screens jiggle in edit mode. A per-tile delay derived
          // from the id desyncs neighbours so they don't wobble in lockstep.
          "@keyframes bookmarkWiggle": {
            "0%": { transform: "rotate(-1.4deg)" },
            "50%": { transform: "rotate(1.4deg)" },
            "100%": { transform: "rotate(-1.4deg)" },
          },
          ...(manageMode &&
            !isDragging && {
              animation: "bookmarkWiggle 0.32s ease-in-out infinite",
              animationDelay: `${(id.length % 4) * 40}ms`,
            }),
          ...(highlighted && {
            outline: "2px solid",
            outlineColor: ringColor,
            outlineOffset: 2,
            boxShadow: onColor
              ? "0 0 0 4px rgba(255, 255, 255, 0.2)"
              : `0 0 0 4px ${theme.palette.primary.main}33`,
          }),
          // dnd-kit only flips isDragging once the pointer moves past its
          // activation threshold, so a plain press-and-hold would show nothing.
          // Mirror the dragging look on :active so picking a tile up in manage
          // mode reads instantly.
          "&:active": manageMode
            ? { opacity: 0.4, cursor: "grabbing" }
            : undefined,
          "&:hover": hoverSx,
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: ringColor,
            outlineOffset: "-2px",
          },
        })}
      >
        {/* The sub-link's parent name sits ABOVE the icon, and out of flow: it
            is absolutely positioned against this wrapper so a tile that has one
            keeps its icon at exactly the same spot as a tile that does not. */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {parentLabel && (
            <Typography
              sx={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                right: 0,
                mb: "2px",
                fontSize: 10,
                lineHeight: 1.2,
                textAlign: "center",
                color: onColor ? "rgba(255, 255, 255, 0.7)" : "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {parentLabel}
            </Typography>
          )}
          {Icon ? (
            <Icon
              sx={{
                fontSize: iconSize,
                color: onColor ? "#FFFFFF" : "#00000080",
              }}
            />
          ) : (
            <BookmarkIcon
              sx={{
                fontSize: iconSize,
                color: onColor ? "#FFFFFF" : "#00000080",
              }}
            />
          )}
        </Box>
        <Box sx={{ maxWidth: "100%", minWidth: "100%" }}>
          <Typography
            sx={{
              fontSize: frosted ? 14 : 13,
              fontWeight: 500,
              lineHeight: 1.15,
              textAlign: "center",
              wordBreak: "break-word",
              color: onColor ? "#FFFFFF" : "text.primary",
              // Cap at two lines so a long label never overflows the square
              // and makes one tile taller than its neighbors.
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {node.label}
          </Typography>
        </Box>
      </Card>

      {manageMode && (
        <IconButton
          size="small"
          aria-label={`Remove ${node.label} bookmark`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(id);
          }}
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(0.5),
            right: theme.spacing(0.5),
            bgcolor: "background.paper",
            boxShadow: 1,
            p: 0.25,
            "&:hover": {
              bgcolor: "background.paper",
              filter: "brightness(0.95)",
            },
          })}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}
